import { Navigate, useLocation } from "react-router-dom";
import { useSubscription } from "../contexts/SubscriptionContext";
import { useUser } from "../contexts/UserContext";
import { useUserStore } from "../stores/useUserStore";
import { hasModuleAccess } from "../utils/permissions";

/**
 * ProtectedRoute component that checks both authentication and subscription status
 * 
 * Route Access Levels:
 * - allowWhenExpired: Only account recovery routes (settings/subscription)
 * - requiresSubscription: Every business workspace route is locked after expiry
 * 
 * During free trial: All features accessible
 * After expiration: only Settings and Subscription remain open
 */
const ProtectedRoute = ({ children, requiresSubscription = true, allowWhenExpired = false, moduleKey = null }) => {
  const { isAuthenticated, authReady } = useUser();
  const { hasValidSubscription, isLoading } = useSubscription();
  const location = useLocation();
  const user = useUserStore((state) => state.user);

  if (!authReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600" aria-label="Restoring your session" />
      </div>
    );
  }

  // Check authentication first
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (
    user?.must_change_password &&
    location.pathname !== "/change-temporary-password"
  ) {
    return <Navigate to="/change-temporary-password" replace />;
  }

  if (moduleKey && !hasModuleAccess(user, moduleKey)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Account recovery routes remain usable after expiry.
  if (allowWhenExpired || location.pathname === "/subscription") {
    return children;
  }

  // Only subscription-protected business routes need to wait for the status
  // request. Onboarding, dashboard recovery and settings must not be held
  // behind an unrelated subscription network request.
  if (requiresSubscription && isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#667eea] mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // For routes that require subscription (sales, payments, purchases, expenses, reports)
  if (requiresSubscription && !hasValidSubscription) {
    // Redirect to subscription page with message
    return <Navigate to="/subscription" state={{ from: location, needsSubscription: true }} replace />;
  }

  return children;
};

export default ProtectedRoute;
